import { useDeleteMyLibraryMutation } from '@/generated/graphql';
import { GET_MY_LIBRARIES } from '@/queries/queries';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface UseDeleteLibraryOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useDeleteLibrary = (options?: UseDeleteLibraryOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [deleteLibraryMutation, { loading }] = useDeleteMyLibraryMutation({
    update: (cache, { data }) => {
      const existingData = cache.readQuery({
        query: GET_MY_LIBRARIES,
      }) as { myLibraries: any[] };
      const newLibraries = existingData?.myLibraries.filter(
        (library: any) => library.id !== data?.deleteMyLibrary?.id
      );
      cache.writeQuery({
        query: GET_MY_LIBRARIES,
        variables: {
          offset: -1
        },
        data: {
          myLibraries: newLibraries,
        },
      });
    },
  });

  const deleteLibrary = async (libraryId: string) => {
    try {
      await deleteLibraryMutation({
        variables: { id: libraryId },
      });

      toast({
        title: t('deleteLibrary.success'),
        description: t('deleteLibrary.successDescription'),
      });

      options?.onSuccess?.();
    } catch (error) {
      console.error('Failed to delete library item', error);

      toast({
        title: t('deleteLibrary.error'),
        description: t('deleteLibrary.errorDescription'),
        variant: 'destructive',
      });

      options?.onError?.(error as Error);
    }
  };

  return {
    deleteLibrary,
    loading,
  };
};
